/**
 * @voltxampere/spice — NetlistDoc → Circuit
 *
 * Sprint 16. The transform walks every element card, dispatches
 * to its codec, and aggregates the resulting components + nets +
 * wires into a fresh `Circuit`. Position auto-layout drops
 * components left-to-right on a 96 px column pitch (so a 10
 * component netlist fits comfortably in the default viewport).
 *
 * Wire generation: SPICE has no wire geometry; for each component
 * terminal we emit a short stub wire from the terminal to a net
 * "rail" anchor. The rail layout is intentionally simple — Sprint
 * 25+ auto-router takes over, but Sprint 16 just needs round-trip
 * correctness and minimal overlap.
 *
 * Schema version is hardcoded as `0.4.0` (Sprint 12 D2 carry-over)
 * since the schema package is locked at that version. When the
 * persistence sprint (Sprint 19+) lands, this constant becomes a
 * generated re-export.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { findCodecsByLetter } from "../element-codecs/index.js";
import { createResolverContext, listNets, resolveNet } from "./net-resolver.js";
const SCHEMA_VERSION_HARDCODE = "0.4.0"; // TODO Sprint 19 persistence
const COLUMN_PITCH = 96; // px between auto-laid components
const ROW_BASELINE = 0;
export function fromSpice(doc, opts = {}) {
    const resolver = createResolverContext();
    const warnings = [];
    const components = [];
    const wires = [];
    let columnIndex = 0;
    for (const card of doc.cards) {
        if (card.kind !== "element")
            continue;
        const codecs = findCodecsByLetter(card.letter);
        if (codecs.length === 0) {
            warnings.push(`Line ${String(card.line)}: unknown SPICE element letter '${card.letter}' — skipped`);
            continue;
        }
        let parsed = null;
        let nodeNames = [];
        let lastError = "";
        for (const codec of codecs) {
            const result = codec.parse(card, { resolver, warnings });
            if (result.ok) {
                parsed = result.component;
                nodeNames = result.nodeNames;
                break;
            }
            lastError = result.error;
        }
        if (parsed === null) {
            warnings.push(`Line ${String(card.line)}: ${card.identifier} — ${lastError.length > 0 ? lastError : "no codec accepted the line"}`);
            continue;
        }
        // Lay out: column based on appearance order.
        const x = columnIndex * COLUMN_PITCH;
        const y = ROW_BASELINE;
        columnIndex += 1;
        // Build terminals from the resolved nets. We use generic
        // terminal IDs (`t0`, `t1`, …) since the codec already
        // returned the node names in terminal order.
        const terminals = nodeNames.map((name, idx) => {
            const net = resolveNet(resolver, name);
            const offsetX = idx === 0 ? -16 : 16;
            return {
                terminalId: `t${String(idx)}`,
                netId: net.id,
                position: { x: x + offsetX, y },
            };
        });
        const placed = {
            ...parsed,
            position: { x, y },
            terminals,
        };
        components.push(placed);
        // Emit a stub wire per terminal so the schematic shows the
        // connection. For the singleton ground we collapse all stubs to
        // the same net rail under the columns row.
        for (const terminal of terminals) {
            const segmentEnd = { x: terminal.position.x, y: y + 32 };
            wires.push({
                id: `${placed.id}-${terminal.terminalId}-wire`,
                netId: terminal.netId,
                path: [terminal.position, segmentEnd],
                style: "orthogonal",
            });
        }
    }
    const nets = listNets(resolver);
    for (const w of resolver.warnings)
        warnings.push(w);
    const now = opts.createdAt ?? Date.now();
    const circuitId = opts.circuitId ?? generateCircuitId();
    const circuit = {
        schemaVersion: SCHEMA_VERSION_HARDCODE,
        id: circuitId,
        name: opts.circuitName ?? (doc.title.length > 0 ? doc.title : "Imported circuit"),
        createdAt: now,
        updatedAt: now,
        nodes: [],
        nets: [...nets],
        components,
        wires,
        groups: [],
        annotations: [],
        assertions: [],
        simulationSettings: {
            defaultAnalysis: "dc",
            dc: {
                temperature: 27,
                gmin: 1e-12,
                reltol: 1e-3,
                abstol: 1e-12,
                maxIterations: 100,
                enableDeltaVDamping: true,
                toleranceStrategy: "strict",
                enableAnderson: false,
                enableArmijo: false,
            },
            transient: {
                tstart: 0,
                tstop: 1e-3,
                method: "trap",
                reltol: 1e-3,
                abstol: 1e-12,
            },
            ac: {
                start: 1,
                stop: 1e9,
                points: 100,
                sweep: "dec",
            },
        },
        metadata: {},
    };
    return { circuit, warnings };
}
/**
 * Generate a UUID-shaped string for imported circuits. Browsers
 * have `crypto.randomUUID`; Node test environments may not, so we
 * fall back to a deterministic-ish builder.
 */
function generateCircuitId() {
    const cryptoLike = globalThis.crypto;
    if (cryptoLike?.randomUUID)
        return cryptoLike.randomUUID();
    // Fallback — not RFC4122 compliant but matches the schema regex
    // used by tests (8-4-4-4-12 hex grouping).
    const seg = (n) => Math.floor(Math.random() * 16 ** n)
        .toString(16)
        .padStart(n, "0");
    return `${seg(8)}-${seg(4)}-4${seg(3)}-${seg(4)}-${seg(12)}`;
}
//# sourceMappingURL=from-spice.js.map